// src/lib/smart-inbox/classifier.ts
// Rule-based email classification engine for Smart Inbox

export interface ClassificationResult {
  action: 'deal' | 'paid' | 'ppsr' | 'inspection' | 'finance' | 'manual';
  confidence: number; // 0-100
  rulesMatched: string[];
  reasoning: string;
}

interface ClassificationRule {
  keywords: string[];
  patterns: RegExp[];
  action: ClassificationResult['action'];
  baseConfidence: number;
  knownDomainKeywords?: string[]; // Specific to this rule
}

const RULES: Record<string, ClassificationRule> = {
  VEHICLE_OFFERED: {
    keywords: [
      'available',
      'selling',
      'interested',
      'asking',
      'for sale',
      'vehicle available',
      'car available',
      'vin:',
      'rego:',
      'registration:',
      'cash',
      'negotiable'
    ],
    patterns: [
      /\$[\d,]+\s*(cash|ono|asking)?/i,
      /\d{4}\s+(toyota|honda|mazda|ford|hyundai|subaru|mitsubishi|nissan|volkswagen|kia|bmw|mercedes)/i,
      /vin:\s*[A-Z0-9]{17}/i,
      /rego:\s*[A-Z0-9]{6}/i,
      /registration:\s*[A-Z0-9]{6}/i,
      /\d+\s*km\s*(kilometers?|miles?)?\s*on\s*the\s*clock/i
    ],
    action: 'deal',
    baseConfidence: 80,
    knownDomainKeywords: ['seller', 'carbay', 'auction', 'private']
  },

  PAYMENT_RECEIVED: {
    keywords: [
      'payment received',
      'payment cleared',
      'transferred',
      'completed',
      'invoice paid',
      'paid in full',
      'funds received',
      'transaction confirmed',
      'bank transfer',
      'receipt'
    ],
    patterns: [
      /da-\d{4}-\d{4}/i,
      /\$[\d,]+\s+(paid|transferred|received)/i,
      /transaction\s*(?:ref|#|number):\s*[A-Z0-9-]+/i,
      /payment\s*(?:ref|#|id):\s*[A-Z0-9-]+/i
    ],
    action: 'paid',
    baseConfidence: 78,
    knownDomainKeywords: ['auction', 'dealer', 'dealership', 'bank']
  },

  PPSR_REPORT: {
    keywords: [
      'ppsr',
      'ppsr report',
      'ppsr certificate',
      'certificate',
      'encumbrance',
      'encumbered',
      'clear',
      'ppsr cloud',
      'encumbrance check',
      'no encumbrances',
      'free and clear'
    ],
    patterns: [
      /ppsr-\d{4}-\d{4}/i,
      /certificate\s*#\s*[A-Z0-9-]+/i,
      /ppsr\s+status:\s*(clear|encumbered)/i
    ],
    action: 'ppsr',
    baseConfidence: 75,
    knownDomainKeywords: ['ppsr', 'ppsrcloud', 'certificate']
  },

  INSPECTION_REPORT: {
    keywords: [
      'inspection',
      'inspection report',
      'inspected',
      'inspection complete',
      'report complete',
      'findings',
      'condition report',
      'pass',
      'fail',
      'mechanical check',
      'safety check'
    ],
    patterns: [
      /inspection\s+(pass|fail|complete)/i,
      /findings:/i,
      /inspection\s+status:\s*(pass|fail)/i,
      /recommend(ed)?\s+(replacement|repair)/i
    ],
    action: 'inspection',
    baseConfidence: 72,
    knownDomainKeywords: ['mechanic', 'inspector', 'garage']
  },

  FINANCE_PAYOUT: {
    keywords: [
      'payout',
      'finance',
      'settlement',
      'discharge',
      'finance payout',
      'settlement amount',
      'outstanding',
      'loan payoff'
    ],
    patterns: [
      /finance\s+payout:\s*\$[\d,]+/i,
      /settlement\s+amount:\s*\$[\d,]+/i,
      /outstanding\s+amount:\s*\$[\d,]+/i
    ],
    action: 'finance',
    baseConfidence: 70,
    knownDomainKeywords: ['finance', 'bank', 'lender']
  }
};

// Known business sender domains give a small corroborating bonus. Personal
// domains (gmail/yahoo/outlook) are intentionally NOT here — they carry no
// signal about the email's purpose and must not lift confidence on their own.
const KNOWN_SENDER_DOMAINS: Record<string, string[]> = {
  'carbay.com.au': ['vehicle', 'seller'],
  'auction.com.au': ['auction', 'dealer'],
  'ppsrcloud.com.au': ['ppsr', 'certificate'],
  'mechanic.com.au': ['inspection', 'report'],
  'finance.com.au': ['payout', 'settlement']
};

/**
 * Scoring is PATTERN-DOMINANT and gated on real signals.
 *
 * The distinguishing evidence for an action is structured data — a $price, a
 * VIN, a deal id, a PPSR cert, a "settlement amount: $…". Generic keywords
 * ("interested", "clear", "pass") are weak on their own and must NOT be enough
 * to auto-suggest a side-effectful action. So:
 *   - a rule with zero keyword AND zero pattern hits is not a candidate;
 *   - with ≥1 pattern hit, the rule earns its baseConfidence + bonuses;
 *   - with keywords only (no pattern), the score is capped well below the 70%
 *     suggest threshold, so it lands in Manual Review.
 * Ties break toward the rule with more pattern hits.
 *
 * (The previous implementation started every rule at its baseConfidence even
 * with no matches, so scores saturated at 100 and classification collapsed to
 * "first rule wins" — misclassifying PPSR/inspection/finance and creating
 * spurious deals from vague emails.)
 */
export function classifyEmail(input: {
  subject: string;
  body: string;
  from_domain: string;
  keywords?: string[];
}): ClassificationResult {
  let bestMatch: ClassificationResult = {
    action: 'manual',
    confidence: 0,
    rulesMatched: [],
    reasoning: 'No matching rules found. Manual review recommended.'
  };
  let bestPatternHits = -1;

  const bodyLower = input.body.toLowerCase();
  const subjectLower = input.subject.toLowerCase();
  const combinedText = `${subjectLower} ${bodyLower}`;
  const domain = input.from_domain.toLowerCase();

  for (const [ruleKey, rule] of Object.entries(RULES)) {
    const matched: string[] = [];

    let keywordHits = 0;
    for (const kw of rule.keywords) {
      if (bodyLower.includes(kw.toLowerCase()) || subjectLower.includes(kw.toLowerCase())) {
        keywordHits++;
        matched.push(`keyword:${kw}`);
      }
    }

    let patternHits = 0;
    for (const pattern of rule.patterns) {
      if (pattern.test(combinedText)) {
        patternHits++;
        matched.push(`pattern:${ruleKey}`);
      }
    }

    // Not applicable at all — skip (do NOT award baseConfidence).
    if (keywordHits === 0 && patternHits === 0) continue;

    // Corroborating sender-domain bonus.
    let domainBonus = 0;
    for (const [dom, dkws] of Object.entries(KNOWN_SENDER_DOMAINS)) {
      if (domain.includes(dom)) {
        domainBonus += 4;
        matched.push(`domain:${dom}`);
        for (const dkw of dkws) {
          if (bodyLower.includes(dkw) || subjectLower.includes(dkw)) {
            domainBonus += 3;
            matched.push(`domain-keyword:${dkw}`);
          }
        }
        break;
      }
    }

    let confidence: number;
    if (patternHits >= 1) {
      // Genuine structured signal — trust the rule.
      confidence = rule.baseConfidence + (patternHits - 1) * 6 + keywordHits * 3 + domainBonus;
    } else {
      // Keywords only — weak; keep it below the 70% suggest threshold.
      confidence = Math.min(60, keywordHits * 12) + domainBonus;
    }
    confidence = Math.min(confidence, 100);

    const better =
      confidence > bestMatch.confidence ||
      (confidence === bestMatch.confidence && patternHits > bestPatternHits);
    if (better) {
      bestPatternHits = patternHits;
      bestMatch = {
        action: rule.action,
        confidence,
        rulesMatched: matched,
        reasoning: generateReasoning(rule.action, matched, confidence)
      };
    }
  }

  return bestMatch;
}

function generateReasoning(
  action: ClassificationResult['action'],
  matched: string[],
  _confidence: number
): string {
  if (matched.length === 0) {
    return 'No matching rules found. Manual review recommended.';
  }

  const descriptions = {
    deal: 'Vehicle offer detected from seller.',
    paid: 'Payment confirmation detected.',
    ppsr: 'PPSR certificate or status report detected.',
    inspection: 'Vehicle inspection report detected.',
    finance: 'Finance payout or settlement information detected.',
    manual: 'Insufficient confidence for automatic action.'
  };

  return `${descriptions[action]} (Matched: ${matched.slice(0, 3).join(', ')}${matched.length > 3 ? `, +${matched.length - 3} more` : ''})`;
}

export function shouldAutoExecute(confidence: number): boolean {
  // Auto-execute actions with >= 75% confidence
  // 70-74% still suggests but requires approval
  // < 70% goes to manual review
  return confidence >= 75;
}

export function shouldSuggest(confidence: number): boolean {
  // Suggest actions with >= 70% confidence
  return confidence >= 70;
}

// Extract VIN from email body using pattern matching
export function extractVIN(text: string): string | null {
  const match = text.match(/vin:\s*([A-Z0-9]{17})/i);
  return match ? match[1].toUpperCase() : null;
}

// Extract rego/registration from email body
export function extractRego(text: string): string | null {
  const match = text.match(/(?:rego|registration):\s*([A-Z0-9]{6})/i);
  return match ? match[1].toUpperCase() : null;
}

// Extract price from email body
export function extractPrice(text: string): number | null {
  const match = text.match(/\$[\s,]*(\d{1,3}(?:,?\d{3})*)/);
  if (match) {
    return parseInt(match[1].replace(/,/g, ''), 10);
  }
  return null;
}

// Extract deal ID pattern (DA-YYYY-NNNN)
export function extractDealID(text: string): string | null {
  const match = text.match(/(DA-\d{4}-\d{4})/i);
  return match ? match[1].toUpperCase() : null;
}

// Clean email body: remove common signatures, HTML, etc.
export function cleanEmailBody(raw: string): string {
  return raw
    .split('\n')
    .filter(line => {
      const trimmed = line.trim();
      // Skip common email signatures and metadata
      if (
        trimmed.startsWith('>') || // Quoted text
        trimmed.startsWith('--') || // Signature divider
        trimmed.startsWith('On ') || // Original message marker
        trimmed.match(/^(Best regards|Cheers|Regards|Sincerely|Thanks)/) ||
        trimmed.match(/^(Sent from|Email confidentiality)/)
      ) {
        return false;
      }
      return trimmed.length > 0;
    })
    .join('\n')
    .trim();
}
